import React, { FC, RefObject } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { scale } from '@components/frame';
import { ModeSelector, TableTools } from '@components/search/components/toolbar';
import { ColorFunction } from '@styles/colors/types';
import { connect } from 'react-redux';
import { DETAILS_SCREEN_TAB_NAME_TO_SCOPE_ID } from '@components/imageHandling/common';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { ProductionBatchStatistics } from '@interfaces/search';
import { DETAILS_SCREEN_TAB_NAME, INFO_SCREEN_DOCUMENTS_DISABLED_MODES } from '@v2/pages/common/constants/base.constants';
import { DisplayMode } from './ContextHeader';
import { LEVELS } from '@helpers';
import { useBuildLinkListByHeaderConfigAndNavigationContext } from '@v2/pages/common/hooks/useBuildLinkListByHeaderConfigAndNavigationContext';
import { ContainerStyle } from '@interfaces/base';

type Documents = Record<string, Record<string, unknown>[]>;

interface DocumentsToolsProps extends ContainerStyle {
  C: ColorFunction;
  activeTabName: string | null;
  ncFileId: number | null;
  partId: number | null;
  jobId: number | null;
  productionOrderId: number | null;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
  documents: Documents;
  setDocuments: (documents: Documents) => Promise<Documents>;
  isWithERP: boolean | null;
  displayMode: DisplayMode;
  setDisplayMode: (displayMode: DisplayMode) => Promise<void>;
  customAnchorRef: RefObject<View>;
  images: any;
  modeSelectorContainerStyle?: StyleProp<ViewStyle>;
  shouldDisplayModeSelector?: boolean;
}

const DocumentsTools: FC<DocumentsToolsProps> = ({
  activeTabName,
  C,
  batch,
  jobId,
  ncFileId,
  partId,
  productionOrderId,
  isWithERP,
  displayMode,
  customAnchorRef,
  setDisplayMode,
  containerStyle,
  modeSelectorContainerStyle,
  shouldDisplayModeSelector = true,
}) => {
  const batchId = batch?.data?.productionBatchId;

  const scopeReferenceIdConfig = {
    [DETAILS_SCREEN_TAB_NAME.Part]: partId,
    [DETAILS_SCREEN_TAB_NAME.Jobs]: jobId,
    [DETAILS_SCREEN_TAB_NAME.Batch]: batchId,
    [DETAILS_SCREEN_TAB_NAME.Order]: productionOrderId,
    [DETAILS_SCREEN_TAB_NAME.NCFile]: ncFileId,
  };

  const getScopeIdScopeReferenceId = () => {
    if (!activeTabName) return null;

    const scopeId = DETAILS_SCREEN_TAB_NAME_TO_SCOPE_ID[activeTabName];
    if (scopeId == null && typeof scopeId !== 'number') return null;

    const scopeReferenceId = scopeReferenceIdConfig[activeTabName];
    if (scopeReferenceId == null) return null;

    return {
      scopeId,
      scopeReferenceId,
    };
  };

  const tag = getScopeIdScopeReferenceId();

  const imageDocumentReferences = useBuildLinkListByHeaderConfigAndNavigationContext();

  const imageUploadConfig = {
    saveToMainStore: true,
    saveToEnd: false,
    onlySelectImage: false,
    uploadEnd: () => {},
    tags: tag ? [tag] : [],
    imageDocumentReferences,
  };

  return (
    <View style={[{ flexDirection: 'row', alignItems: 'flex-start', gap: scale(18) }, containerStyle]}>
      {shouldDisplayModeSelector && (
        <View style={[modeSelectorContainerStyle]}>
          <ModeSelector
            {...{
              C,
              displayMode,
              setSelectedDisplayMode: setDisplayMode,
              level: LEVELS.DetailsScreenImagesDocuments,
              disabledModes: INFO_SCREEN_DOCUMENTS_DISABLED_MODES,
            }}
          />
        </View>
      )}
      <TableTools
        ref={customAnchorRef}
        {...{
          C,
          isRefReady: false,
          isTableMode: false,
          level: LEVELS.DetailsScreenImagesDocuments,
          isWithERP: !!isWithERP,
          size: 29,
          imageUploadConfig,
        }}
      />
    </View>
  );
};

export default connect(
  ({ details: { ncFileId, partId, jobId, productionOrderId }, batch: { batch }, membership: { isWithERP } }) => ({
    ncFileId,
    partId,
    jobId,
    batch,
    productionOrderId,
    isWithERP,
  }),
  {}
)(DocumentsTools);
